/**
 * Auth Context - Håndterer autentisering globalt
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth må brukes innenfor AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || 'https://advokat-crm-api.swagner.workers.dev';

  // Debug: Log API URL
  useEffect(() => {
    console.log('AuthContext API_URL:', API_URL);
  }, []);

  // Sjekk om bruker er innlogget (ved app start)
  useEffect(() => {
    const savedToken = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');

    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
    }

    setLoading(false);
  }, []);

  // Login
  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Innlogging feilet');
      }

      const data = await response.json();

      // Lagre token og bruker
      localStorage.setItem('access_token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      setToken(data.token);
      setUser(data.user);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Registrer ny bruker
  const register = async (email, password, name) => {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, name }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Registrering feilet');
      }

      // Logg inn automatisk etter registrering
      return await login(email, password);
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Logg ut
  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('authToken'); // Fjern gammel nøkkel også
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  // Microsoft 365-autentisering
  const loginWithMicrosoft = async () => {
    try {
      // Hent Microsoft auth URL fra backend
      const response = await fetch(`${API_URL}/auth/microsoft/url`);
      const data = await response.json();

      // Redirect til Microsoft login
      window.location.href = data.authUrl;
    } catch (error) {
      console.error('Microsoft login error:', error);
      return { success: false, error: error.message };
    }
  };

  // Håndter Microsoft callback
  const handleMicrosoftCallback = async (code) => {
    try {
      const response = await fetch(`${API_URL}/auth/microsoft/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        throw new Error('Microsoft authentication failed');
      }

      const data = await response.json();

      // Lagre token og bruker
      localStorage.setItem('access_token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      setToken(data.token);
      setUser(data.user);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // API helper med auto-auth
  const api = async (endpoint, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${API_URL}${endpoint}`;
    console.log('API call:', { url, hasToken: !!token });

    const response = await fetch(url, {
      ...options,
      headers,
    });

    console.log('API response:', { url, status: response.status, ok: response.ok });

    if (response.status === 401) {
      // Token utløpt - logg ut
      logout();
      throw new Error('Session utløpt - vennligst logg inn igjen');
    }

    return response;
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    login,
    register,
    logout,
    loginWithMicrosoft,
    handleMicrosoftCallback,
    api,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
